/**
 * Friends-tab badge — small red dot with a count of incoming friend
 * requests + incoming challenges. Drives the "you have something
 * waiting" cue on every page, so a user who lands on /wallet or /ranks
 * still notices a pending invite without opening the Friends tab.
 *
 * Polls /friends/requests + /challenges/incoming on a slow cadence
 * (60s) while the tab is visible. The friends page also calls
 * `refreshFriendsBadge()` after acting on a row so the count drops
 * immediately rather than waiting for the next tick.
 */

import { api } from '../api.js';
import { auth } from '../auth.js';

const POLL_INTERVAL_MS = 60_000;

let _count = 0;
let _timer = null;
let _started = false;

function _badgeEl() {
  return document.getElementById('tab-badge-friends');
}

/** Paint the current cached count to the DOM. Called by both the
 *  polling tick and `renderTabbar` (so a route change that re-renders
 *  the tab bar doesn't blank the badge). */
export function applyFriendsBadge() {
  const el = _badgeEl();
  if (!el) return;
  if (_count <= 0) {
    el.style.display = 'none';
    el.textContent = '';
    return;
  }
  el.textContent = _count > 99 ? '99+' : String(_count);
  el.style.display = 'inline-flex';
}

async function _fetch() {
  if (!auth.isAuthenticated()) {
    _count = 0;
    applyFriendsBadge();
    return;
  }
  if (document.hidden) return;
  try {
    const [reqs, chals] = await Promise.all([
      api('/friends/requests'),
      api('/challenges/incoming'),
    ]);
    _count = (reqs?.length || 0) + (chals?.length || 0);
    applyFriendsBadge();
  } catch {
    // Best-effort — a transient network blip should not surface here.
  }
}

export function startFriendsBadgePolling() {
  if (_started) return;
  _started = true;
  _fetch();
  _timer = setInterval(_fetch, POLL_INTERVAL_MS);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) _fetch();
  });
}

/** Force-refresh the badge from the API. Friends-page actions
 *  (accept/decline/cancel) call this so the count drops without
 *  waiting for the 60s poll. */
export function refreshFriendsBadge() {
  return _fetch();
}
